import { isCpf, maskCpf } from './cpf';
import { normalizeDocument, onlyDigits } from './normalize';

export const CNPJ_LENGTH = 14;
const CNPJ_BASE_LENGTH = 12;

const WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const INVALID_NUMERIC_CNPJS = new Set([
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999',
]);

export type DocumentType =
  | 'cpf'
  | 'cnpj_numeric'
  | 'cnpj_alphanumeric'
  | 'invalid';

export { normalizeDocument, onlyDigits };

export function isAlphanumericCnpjEnabled(): boolean {
  const flag =
    process.env.REACT_APP_CNPJ_ALPHANUMERIC_ENABLED ??
    process.env.CNPJ_ALPHANUMERIC_ENABLED;
  if (flag === undefined || flag === '') return true;
  return flag === 'true' || flag === '1';
}

export function charValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

function calculateCheckDigit(base: string, weights: number[]): number {
  const sum = base
    .split('')
    .reduce((acc, char, index) => acc + charValue(char) * weights[index], 0);
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function isCnpjNumeric(value: string): boolean {
  const cnpj = normalizeDocument(value);
  if (cnpj.length !== CNPJ_LENGTH || !/^\d{14}$/.test(cnpj)) {
    return false;
  }
  if (INVALID_NUMERIC_CNPJS.has(cnpj)) return false;

  const dv1 = calculateCheckDigit(cnpj.slice(0, CNPJ_BASE_LENGTH), WEIGHTS_DV1);
  if (dv1 !== parseInt(cnpj.charAt(12), 10)) return false;

  const dv2 = calculateCheckDigit(cnpj.slice(0, 13), WEIGHTS_DV2);
  return dv2 === parseInt(cnpj.charAt(13), 10);
}

export function isCnpjAlphanumeric(value: string): boolean {
  const cnpj = normalizeDocument(value);
  if (cnpj.length !== CNPJ_LENGTH) return false;

  const base = cnpj.slice(0, CNPJ_BASE_LENGTH);
  const dv = cnpj.slice(CNPJ_BASE_LENGTH);

  if (!/^[0-9A-Z]{12}$/.test(base)) return false;
  if (!/^\d{2}$/.test(dv)) return false;
  if (!/[A-Z]/.test(base)) return false;

  const dv1 = calculateCheckDigit(base, WEIGHTS_DV1);
  if (dv1 !== parseInt(dv.charAt(0), 10)) return false;

  const dv2 = calculateCheckDigit(cnpj.slice(0, 13), WEIGHTS_DV2);
  return dv2 === parseInt(dv.charAt(1), 10);
}

export function isCnpj(value: string): boolean {
  const cnpj = normalizeDocument(value);
  if (cnpj.length !== CNPJ_LENGTH) return false;

  const hasLetters = /[A-Z]/.test(cnpj.slice(0, CNPJ_BASE_LENGTH));
  if (hasLetters) {
    if (!isAlphanumericCnpjEnabled()) return false;
    return isCnpjAlphanumeric(cnpj);
  }
  return isCnpjNumeric(cnpj);
}

export function getDocumentType(value?: string | null): DocumentType {
  const doc = normalizeDocument(value);
  if (doc.length === 11 && /^\d{11}$/.test(doc)) {
    return isCpf(doc) ? 'cpf' : 'invalid';
  }
  if (doc.length !== CNPJ_LENGTH) return 'invalid';

  const hasLetters = /[A-Z]/.test(doc.slice(0, CNPJ_BASE_LENGTH));
  if (hasLetters) {
    return isCnpjAlphanumeric(doc) ? 'cnpj_alphanumeric' : 'invalid';
  }
  return isCnpjNumeric(doc) ? 'cnpj_numeric' : 'invalid';
}

export function maskCnpj(value: string): string {
  const raw = normalizeDocument(value).slice(0, CNPJ_LENGTH);
  if (!raw) return '';

  let result = raw.slice(0, 2);
  if (raw.length > 2) result += '.' + raw.slice(2, Math.min(5, raw.length));
  if (raw.length > 5) result += '.' + raw.slice(5, Math.min(8, raw.length));
  if (raw.length > 8) result += '/' + raw.slice(8, Math.min(12, raw.length));
  if (raw.length > 12) result += '-' + raw.slice(12, 14);
  return result;
}

export function unmaskCpfOrCnpj(value: string): string {
  return normalizeDocument(value);
}

export function maskCpfOrCnpj(value: string): string {
  const normalized = normalizeDocument(value);
  if (!normalized) return '';

  if (normalized.length <= 11) {
    return maskCpf(normalized);
  }
  return maskCnpj(normalized);
}

export function splitCnpj(cnpj: string): {
  base: string;
  branch: string;
  dv: string;
} {
  const n = normalizeDocument(cnpj);
  return {
    base: n.slice(0, 8),
    branch: n.slice(8, 12),
    dv: n.slice(12, 14),
  };
}

/** Prefix leading zero for legacy numeric CNPJ in Quiver XML (12-digit base). */
export function formatCnpjForQuiver(cnpj: string): string {
  const n = normalizeDocument(cnpj);
  if (/[A-Z]/.test(n.slice(0, CNPJ_BASE_LENGTH))) {
    return n;
  }
  return n.length === 14 && /^\d+$/.test(n) ? `0${n}` : n;
}

export function isCpfOrCnpj(value: string): boolean {
  const doc = normalizeDocument(value);
  if (doc.length === 11) return isCpf(doc);
  if (doc.length === 14) return isCnpj(doc);
  return false;
}

/** CPF (11 dígitos) ou CNPJ (14 chars) para `estates.cnpj` e campos mistos. */
export function normalizeEstateDocument(value?: string | null): string {
  const n = normalizeDocument(value);
  if (!n) return '';
  if (n.length <= 11) return onlyDigits(n);
  return n.slice(0, CNPJ_LENGTH);
}

export { isCpf };
